import { useAnalysisStore } from '../../store/useAnalysisStore';
import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { CircularProgress } from '@mui/material';
import { useRemoveReportVideoMutation, useRemoveReportMutation } from '../../queries';
import { TALENT_ARR, ANALYSIS_STATUS } from '../../data/define';
import ImgEmpty from '../../assets/image/img_kidface.png';
import IcPlus from '../../assets/image/ic_plus_gray_circle.svg';
import IcRemove from '../../assets/image/ic_x_gray_circle.svg';
import ToolTip from '../common/ToolTip';
import OneBtnAlert from '../common/OneBtnAlert';
import TwoBtnAlert from '../common/TwoBtnAlert';
import { useTranslation } from '../../hook/useTranslation';

const MAX_VIDEO_COUNT = 12;

const ACTION = {
  ADD: 'ADD',
  DELETE: 'DELETE',
};

function VideoList({
  videos = [],
  intelligences = [],
  selectedReportId = null,
  reload = () => {},
  reloadReports = () => {},
  isSuccess = false,
  setIsOpenToggleBtn,
}: any) {

  const { t } = useTranslation();
  const analysisResult = useAnalysisStore((s) => s.analysisResult);
  const [loading, setLoading] = useState<string | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState<{ deleteInfo: string | null; complete: boolean }>({ deleteInfo: null, complete: false });
  const [videoAnalysisAlertOpen, setVideoAnalysisAlertOpen] = useState<string | null>(null);

  const { mutate: removeReportVideo } = useRemoveReportVideoMutation();
  const { mutate: removeReport } = useRemoveReportMutation();

  const handleDeleteVideo = useCallback(() => {
    if (!deleteAlertOpen.deleteInfo) return;
    const deleteInfo = deleteAlertOpen.deleteInfo;
    setLoading(deleteInfo as any);
    setDeleteAlertOpen({ deleteInfo: null, complete: false });
    const isLastVideo = videos?.length === 1;

    removeReportVideo(deleteInfo as string, {
      onSuccess: () => {
        if (isLastVideo && selectedReportId) {
          removeReport(selectedReportId, {
            onSuccess: (res: any) => {
              if (res && res.data?.success) {
                reloadReports();
                setDeleteAlertOpen({ deleteInfo: null, complete: true });
              } else {
                toast.error(t('report.video_list.toast_report_delete_fail'));
              }
            },
            onError: (error) => {
              console.error('Error deleting report:', error);
              toast.error(t('report.video_list.toast_report_delete_fail'));
            },
            onSettled: () => {
              setLoading(null);
            },
          });
        } else {
          reload();
          setDeleteAlertOpen({ deleteInfo: null, complete: true });
          setLoading(null);
        }
      },
      onError: (error) => {
        console.error(`Error deleting video with url`, error);
        toast.error(t('report.video_list.toast_video_delete_fail'));
        setLoading(null);
      },
    });
  }, [
    deleteAlertOpen,
    reload,
    reloadReports,
    selectedReportId,
    videos,
    removeReportVideo,
    removeReport,
    t,
  ]);

  const handleAddVideo = useCallback(() => {
    (setIsOpenToggleBtn as any)(true);
  }, [setIsOpenToggleBtn]);

  return (
    <div className="w-full py-5 flex flex-col justify-start items-center gap-[32px] min-h-[200px] text-[#1C1C1C] text-[16px] font-[700] font-['Pretendard'] bg-white">
      {/* 업로드 영상목록 */}
      <div className="w-full flex flex-col items-start justify-start">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center justify-start gap-2">
            <div className="text-left font-[700] text-[20px]">{t('report.video_list.title')}</div>
            <ToolTip
              open={tooltipOpen}
              setOpen={setTooltipOpen}
              text={t('report.video_list.tooltip')}
            />
          </div>
          <div className="font-[400] text-[14px] text-[#767676]">
            <span className="font-[600]">{videos?.length}</span>
            <span>
              /
              {!isSuccess ? (
                '12'
              ) : (
                <>
                  {videos?.length <= 4 && '4'}
                  {videos?.length >= 5 && videos?.length <= 8 && '8'}
                  {videos?.length >= 9 && videos?.length <= 12 && '12'}
                </>
              )}
              {t('report.video_list.unit')}
            </span>
          </div>
        </div>
        <div className="w-full mt-[16px] grid grid-cols-4 grid-row-auto items-center justify-start gap-2 text-[#303133]">
          {videos?.length >= 1 &&
            videos?.map((video, idx) => {
              return (
                <div
                  key={`video-${idx}`}
                  className="w-full relative h-[100px] flex flex-col items-center justify-center bg-[#F5F5F5] rounded-[10px]"
                >
                  <img
                    src={video.thumbnailUrl || ImgEmpty}
                    alt={video.title}
                    className="w-full h-full object-cover rounded-[10px]"
                    style={video.thumbnailUrl ? {} : { width: '50px', height: '50px' }}
                  />
                  {!isSuccess && (
                    <button
                      type="button"
                      className="absolute top-0 right-0 rounded-full p-1"
                      onClick={() => {
                        if (analysisResult.status === ANALYSIS_STATUS.PENDING) {
                          setVideoAnalysisAlertOpen(ACTION.DELETE);
                        } else {
                          setDeleteAlertOpen({ deleteInfo: video.mediaUrl, complete: false });
                        }
                      }}
                    >
                      <img src={IcRemove} alt="remove" />
                    </button>
                  )}
                  {loading !== null && loading === video.mediaUrl && (
                    <div className="z-1 absolute-center">
                      <CircularProgress size={24} />
                    </div>
                  )}
                </div>
              );
            })}
          {!isSuccess && (
            <>
              {Array.from({ length: MAX_VIDEO_COUNT - videos?.length }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full h-[100px] border border-[#EAEAEA] flex items-center justify-center bg-[#F5F5F5] rounded-[10px]"
                  onClick={() => {
                    if (analysisResult.status === ANALYSIS_STATUS.PENDING) {
                      setVideoAnalysisAlertOpen(ACTION.ADD);
                    } else {
                      handleAddVideo();
                    }
                  }}
                >
                  <img src={IcPlus} className="w-[24px] h-[24px] shrink-0" alt="Add Video" />
                </button>
              ))}
            </>
          )}
          <TwoBtnAlert
            open={deleteAlertOpen.deleteInfo !== null && deleteAlertOpen.complete === false}
            onClose={() => setDeleteAlertOpen({ deleteInfo: null, complete: false })}
            title={t('report.video_list.alert_delete_title')}
            content={t('report.video_list.alert_delete_content')}
            leftBtnText={t('common.cancel')}
            rightBtnText={t('common.confirm')}
            leftBtnClick={() => setDeleteAlertOpen({ deleteInfo: null, complete: false })}
            rightBtnClick={handleDeleteVideo}
          />
          <OneBtnAlert
            open={deleteAlertOpen.complete === true}
            onClose={() => setDeleteAlertOpen({ deleteInfo: null, complete: false })}
            title={t('report.video_list.alert_delete_success_title')}
            description={t('report.video_list.alert_delete_success_desc')}
            btnText={t('common.confirm')}
            onClick={() => setDeleteAlertOpen({ deleteInfo: null, complete: false })}
          />
          <OneBtnAlert
            open={videoAnalysisAlertOpen !== null}
            onClose={() => setVideoAnalysisAlertOpen(null)}
            title={
              videoAnalysisAlertOpen === ACTION.DELETE
                ? t('report.video_list.alert_delete_fail_title')
                : t('nav.alert.analysis_in_progress.title')
            }
            description={
              videoAnalysisAlertOpen === ACTION.DELETE
                ? t('report.video_list.alert_delete_fail_desc')
                : t('nav.alert.analysis_in_progress.description')
            }
            btnText={t('common.confirm')}
            onClick={() => setVideoAnalysisAlertOpen(null)}
          />
        </div>
      </div>
      {/* 분석완료된 지능 */}
      {!isSuccess && (
        <div className="w-full">
          <div className="w-full flex items-center justify-between">
            <div className="w-full flex items-center justify-start gap-2 text-left font-[700] text-[20px]">
              {t('report.video_list.completed_intelligence')}
            </div>
            <div className="font-[400] text-[14px] text-[#767676]">
              <span className="font-[600] text-[#767676]">{intelligences?.length}</span>
              <span>{t('report.need_more.total_count')}</span>
            </div>
          </div>
          <div style={{ width: '100vw', marginLeft: '-20px', marginRight: '-20px' }}>
            <div
              className="mt-[16px] flex items-center justify-start overflow-x-auto pb-2 hide-scrollbar"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              <div className="shrink-0 inline-flex items-center justify-start px-2 gap-2 pl-[20px]">
                {intelligences?.length >= 1 &&
                  [...intelligences]
                    .sort((a, b) => (b?.score || 0) - (a?.score || 0))
                    .map((intel, index) => {
                      const target = TALENT_ARR.find((tlt) => tlt.id === intel.intelligence);
                      return (
                        <div
                          key={`${intel.intelligence}-${index}`}
                          className="bg-[#F0FCFC] shrink-0 rounded-[15px] px-3 py-1 text-[14px] text-[#5EBEBB]"
                        >
                          {target ? t('talent.' + target.key) : ''}&nbsp;
                          {t('report.summary.score', { score: intel?.score })}
                        </div>
                      );
                    })}
              </div>
              <div className="shrink-0 w-[20px] h-[10px]" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoList;
